import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import { de } from './lang/de';

export default class Lightbox {
  constructor(config) {
    this.defaultConfig = {
      pswpModule: () => import('photoswipe'),
      zoom: true,
      returnFocus: true,
      arrowKeys: true,
      escKey: true,
      preloadFirstSlide: false,
      pinchToClose: false,
      clickToCloseNonZoomable: false,
      showHideAnimationType: 'fade',
      padding: {
        top: 100,
        bottom: 100,
      },
      // Custom props
      wcagOptimized: true,
      hasCaption: true,
      hasTitle: true,
      hasCopyright: true,
      defaultDialogTitle: this.localization().defaultDialogTitle,
      figureFix: true,
      ariaRoleDescriptionFix: true,
      disableOnClickZoom: false,
      appendUIElementsToBottom: ['counter', 'arrowPrev', 'arrowNext'],
      focusTrap: {},
      focusTrapDisabled: false,
      focusTrapConfig: {}, // see https://github.com/focus-trap/focus-trap
      alignCopyrightWithImage: false,
      showCopyrightUnderCaption: false,
    };
    this.config = {
      ...this.defaultConfig,
      ...this.localization(),
      ...config,
    };
    this.pswpLightboxInstance = undefined;
    this.slideItems = [];
    this.uiElements = {};
  }

  localization() {
    const lang = document.documentElement.lang;
    const isGerman =
      lang === 'de' || !!document.documentElement.lang.match(/^de-/g);
    if (isGerman) {
      return de;
    }
    return {};
  }

  init() {
    this.pswpLightboxInstance = new PhotoSwipeLightbox(this.config);

    // Map data from dataset attributes
    this.pswpLightboxInstance.addFilter("itemData", (itemData, index) => {
      if (!itemData.element) return itemData;

      const data = itemData.element.dataset;
      return {
        src: data.pswpSrc,
        srcset: data.pswpSrcset,
        width: data.pswpWidth,
        height: data.pswpHeight,
        title: data.pswpTitle,
        alt: data.pswpAlt,
        caption: data.pswpCaption,
        copyright: data.pswpCopyright,
      };
    });

    // Pre-initialization setup
    this.setupLightbox();

    // Initialize Photoswipe instance
    this.pswpLightboxInstance.init();
  }

  destroy() {
    // Destroy Photoswipe instance
    this.pswpLightboxInstance.destroy();
  };

  setupLightbox() {
    // --- Fixes ---
    // IF zoom in config is false also prevent zoomability on image via click.
    if (this.config.disableOnClickZoom) {
      this.pswpLightboxInstance.addFilter('isContentZoomable', () => {
        return false;
      });
      import('./style/disable-on-click-zoom.scss');
    }

    // Remove confusing, non-loaclized and unnecessary `aria-roledescription` set natively by Photoswipe.
    if (this.config.ariaRoleDescriptionFix) this.ariaRoleDescriptionFix();

    // Render <img> inside <figure> with <figcaption>
    if (this.config.figureFix) this.figureFix();

    // --- Preparation ---
    // Add custom UI components
    this.addCustomUI();

    // Add/handle classes
    this.classHandler();

    if (this.config.wcagOptimized) {
      // Add/handle aria attribute
      this.ariaHandler();
      this.focusHandler();
      import('./style/tooltips.scss');
    }

    // On 'initialLayout' is dispatched on every opening of the lightbox.
    this.pswpLightboxInstance.on('initialLayout', () => {
      // Update additional instance infos
      this.uiElements = []; // Reset to ensure retrieval of the currently generated UI elements.
      this.updateSlideItems(); // Fetch the current slide items.
      // Move (default) UI elements
      if (this.config.appendUIElementsToBottom)
        this.moveUIElements(
          this.config.appendUIElementsToBottom,
          this.pswpLightboxInstance.pswp.scrollWrap
        );
    });

    // On 'resize' is dispatched on every layout re-arranging caused by a window resize.
    this.pswpLightboxInstance.on('resize', () => {
      this.copyrightAlignmentHandler();
    });
  }

  updateSlideItems() {
    this.slideItems = [
      ...this.pswpLightboxInstance.pswp.container.querySelectorAll(
        '.pswp__item'
      ),
    ];
  }

  getUIElement(elementName) {
    const uiElementsData = this.pswpLightboxInstance.pswp.ui.uiElementsData;
    const uiElement = {
      element: undefined,
      data: {},
    };

    // If UI element was already retrieved return result skipping the for-loop.
    if (
      this.uiElements[elementName]?.element &&
      Object.keys(this.uiElements[elementName]?.data).length !== 0
    ) {
      return this.uiElements[elementName];
    }

    uiElementsData.forEach(ui => {
      if (ui.name === elementName) {
        const element =
          this.pswpLightboxInstance.pswp.element.querySelector(
            `.${ui.className}`
          ) ||
          this.pswpLightboxInstance.pswp.element.querySelector(
            `.pswp__${ui.name}`
          ) ||
          this.pswpLightboxInstance.pswp.element.querySelector(
            `.pswp__button--${ui.name}`
          );
        uiElement.element = element;
        uiElement.data = ui;
      }
    });

    this.uiElements[elementName] = uiElement;
    return uiElement;
  }

  moveUIElements(uiElements, targetEl) {
    uiElements.forEach(uiElementName => {
      const uiElement = this.getUIElement(uiElementName);
      if (!uiElement?.element) return;

      targetEl.appendChild(
        this.pswpLightboxInstance.pswp.applyFilters(
          'uiElement',
          uiElement.element,
          uiElement.data
        )
      );
    });
  }

  // manipulateDefaultUI() {
  //   this.pswpLightboxInstance.pswp.ui.uiElementsData.forEach((ui, i) => {
  //     if (ui.name === '...')
  //       this.pswpLightboxInstance.pswp.ui.uiElementsData[i].appendTo = '...'
  //   });
  // }

  addCustomUI() {
    this.pswpLightboxInstance.on('uiRegister', () => {
      if (this.config.hasTitle) this.customUITitle();
      if (this.config.hasCaption) this.customUICaption();
    });

    import('./style/hide-unused-data-ui.scss');
    if (this.config.alignCopyrightWithImage)
      import('./style/align-copyright-with-image.scss');
  }

  customUITitle() {
    this.pswpLightboxInstance.pswp.ui.registerElement({
      name: 'title',
      order: 1,
      isButton: false,
      className: 'pswp__heading',
      tagName: 'h1',
      appendTo: 'bar',
      html: this.config.defaultDialogTitle,
      onInit: (el, pswp) => {
        if (pswp.options.padding.top > 0) {
          new Promise(resolve => {
            resolve(pswp.topBar);
          }).then(() => {
            pswp.topBar.style.height = `${pswp.options.padding.top}px`;
          });
        }
        pswp.on('change', () => {
          const title = pswp.currSlide.data.title;
          if (!title) {
            // Since a check for item data can only be done after component registration
            // (unfortunately no conditional registration possible) the title is hidden and defaults to a string chosen in this.config
            // to at least give a semantically necessary title to the dialog.
            el.classList.add('pswp__heading--hidden');
            return;
          }
          el.classList.remove('pswp__heading--hidden');
          el.innerHTML = title;
        });
      },
    });
    import('./style/custom-ui-title.scss');
  }

  customUICaption() {
    this.pswpLightboxInstance.pswp.ui.registerElement({
      name: 'caption',
      order: 9,
      isButton: false,
      className: 'pswp__figcaption',
      tagName: this.config.figureFix ? 'figcaption' : 'div',
      appendTo: 'wrapper',
      html: 'Caption',
      onInit: (el, pswp) => {
        if (pswp.options.padding.bottom > 0) {
          new Promise(resolve => {
            resolve(el);
          }).then(() => {
            el.style.height = `${pswp.options.padding.bottom}px`;
          });
        }
        pswp.on('change', () => {
          const caption = pswp.currSlide.data.caption;
          const copyright = pswp.currSlide.data.copyright;
          let html = `
            <div class='pswp__copyright'>${copyright}</div>
            <div class='pswp__caption'>${caption}</div>
          `;

          // Handle empty caption
          if (!caption && !copyright) {
            // Since a check for item data can only be done after component registration
            // (unfortunately no conditional registration possible) the caption is set to display: none
            // if no data for caption and copyright was given for this item.
            el.classList.add('pswp__figcaption--removed');
            return;
          }
          el.classList.remove('pswp__figcaption--removed');

          // Switch order of caption and copyright if necessary.
          if (this.config.showCopyrightUnderCaption)
            html = `
              <div class='pswp__caption'>${caption}</div>
              <div class='pswp__copyright'>${copyright}</div>
            `;

          // Refresh HTML
          el.innerHTML = html;

          // Refresh inline styles
          if (this.config.figureFix) {
            const transform = pswp.currSlide.holderElement.style.transform;
            el.style.transform = transform;
          }
          this.copyrightAlignmentHandler();
        });
      },
    });
  }

  classHandler() {
    // On 'initialLayout'
    this.pswpLightboxInstance.on('initialLayout', () => {
      const title = this.getUIElement('title').element;
      const dialog = this.pswpLightboxInstance.pswp.element;
      title?.setAttribute('id', 'pswp-heading');
      dialog.setAttribute('id', 'pswp-dialog');
    });

    // On 'change'
    this.pswpLightboxInstance.on('change', () => {
      const currentItem =
        this.pswpLightboxInstance.pswp.currSlide.holderElement;
      const inactiveItems = this.slideItems.filter(
        item => item !== currentItem
      );

      // Current item
      currentItem.classList.add('pswp__item--current');
      inactiveItems.forEach(item => {
        item.classList.remove('pswp__item--current');
      });

      if (this.config.disableOnClickZoom) {
        this.pswpLightboxInstance.pswp.element.classList.add(
          'pswp--disabled-zoom-on-click'
        );
      }
    });
  }

  focusHandler() {
    this.addFocusTrap();
    this.setInitialFocus();
    this.focusContentOnPrevNext();
  }

  ariaHandler() {
    // On 'initialLayout'
    this.pswpLightboxInstance.on('initialLayout', () => {
      const pswp = this.pswpLightboxInstance.pswp;
      const pswpElement = pswp.element;
      const counter = this.getUIElement('counter').element;
      const preloader = this.getUIElement('preloader').element;
      const prev = this.getUIElement('arrowPrev').element;
      const next = this.getUIElement('arrowNext').element;

      // Dialog
      pswpElement.setAttribute('aria-labelledby', 'pswp-heading');
      pswpElement.setAttribute('aria-describedby', 'pswp-figure');

      // Counter
      counter.setAttribute('aria-live', 'polite');
      counter.setAttribute('aria-atomic', true);

      // Preloader
      preloader.setAttribute('aria-hidden', true);

      // Prev
      prev.setAttribute('aria-controls', 'pswp-dialog');

      // Next
      next.setAttribute('aria-controls', 'pswp-dialog');
    });

    // On 'bindEvents'
    this.pswpLightboxInstance.on('bindEvents', () => {
      const element = this.getUIElement('close').element;
      element.focus();
    });

    // On 'change'
    this.pswpLightboxInstance.on('change', () => {
      const currentItem =
        this.pswpLightboxInstance.pswp.currSlide.holderElement;
      const inactiveItems = this.slideItems.filter(
        item => item !== currentItem
      );

      // Current item
      currentItem.setAttribute('aria-hidden', false);
      inactiveItems.forEach(item => {
        item.setAttribute('aria-hidden', true);
      });
    });

    // On 'loadError'
    this.pswpLightboxInstance.on('loadError', () => {
      // Loading error
      const error =
        this.pswpLightboxInstance.pswp.element.querySelector(
          '.pswp__error-msg'
        );
      const msg = error.innerHTML;
      error.setAttribute('aria-live', 'polite');
      error.innerHTML = '';
      error.innerHTML = msg;
    });
  }

  addFocusTrap() {
    if (this.config.focusTrapDisabled) return;
    this.pswpLightboxInstance.on('initialLayout', () => {
      import('focus-trap').then(focusTrap => {
        const dialog = this.pswpLightboxInstance.pswp.element;
        this.focusTrap = focusTrap.createFocusTrap(
          dialog,
          this.config.focusTrapConfig
        );
        this.focusTrap.activate();

        this.pswpLightboxInstance.on('close', () => {
          this.focusTrap.deactivate({
            onPostDeactivate: () => {
              this.removeFocusTrap();
            },
          });
        });
      });
    });
  }

  removeFocusTrap() {
    delete this.focusTrap;
  }

  setInitialFocus() {
    this.pswpLightboxInstance.on('bindEvents', () => {
      const element = this.getUIElement('close').element;
      element.focus();
    });
  }

  focusContentOnPrevNext() {
    this.pswpLightboxInstance.on('slideActivate', () => {
      this.pswpLightboxInstance.pswp.element.focus();
      // Focusing the container is not possible. Shifting the focus to the container leads to a bug,
      // if the prev option is used directly with the initial slide/state:
      // All UI elements blank out. Also the current slide seems to get confused.
      // this.pswpLightboxInstance.pswp.container.focus();
    });
  }

  figureFix() {
    // On 'firstUpdate'
    this.pswpLightboxInstance.on('firstUpdate', () => {
      const container = this.pswpLightboxInstance.pswp.container;
      const figure = document.createElement('figure');

      // Wrap container in <figure> element.
      figure.setAttribute('id', 'pswp-figure');
      figure.setAttribute('tabindex', -1);
      figure.classList.add('pswp__figure');
      figure.append(container);
      this.pswpLightboxInstance.pswp.container = figure;
      this.pswpLightboxInstance.pswp.scrollWrap.append(
        this.pswpLightboxInstance.pswp.container
      );

      // Load CSS
      import('./style/figure-fix.scss');
    });

    // On 'initialLayout'
    this.pswpLightboxInstance.on('initialLayout', () => {
      this.figureFixMoveCaption(['caption']);
    });
  }

  figureFixMoveCaption(uiElements) {
    const container = this.pswpLightboxInstance.pswp.container;
    this.moveUIElements(uiElements, container);
  }

  ariaRoleDescriptionFix() {
    // On 'firstUpdate'
    this.pswpLightboxInstance.on('firstUpdate', () => {
      const pswp = this.pswpLightboxInstance.pswp;
      const pswpElement = pswp.element;
      const ariaRoleDescriptions = pswpElement.querySelectorAll('[aria-roledescription]');

      ariaRoleDescriptions.forEach(el => {
        el.removeAttribute('aria-roledescription');
      });
    });
  }

  copyrightAlignmentHandler() {
    if (this.config.alignCopyrightWithImage) {
      const currentItem =
        this.pswpLightboxInstance.pswp.container.querySelector(
          '.pswp__item--current'
        );
      const imageWidth = currentItem?.querySelector('.pswp__img')?.clientWidth;
      const copyright =
        this.pswpLightboxInstance.pswp.container.querySelector(
          '.pswp__copyright'
        );
      if (copyright) copyright.style.maxWidth = `${imageWidth}px`;
    }
  }
}

export { Lightbox };
